import React from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  TrendingUp as TrendingIcon,
  School as SchoolIcon,
  AccountBalance as BankIcon,
  Share as ShareIcon,
  Assessment as AnalyticsIcon
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    background: 'linear-gradient(135deg, #1760d3 0%, #0d4a9e 45%, #17dddd 100%)',
    minHeight: '100vh'
  },
  hero: {
    textAlign: 'center',
    color: 'white',
    marginBottom: theme.spacing(6)
  },
  heroTitle: {
    fontSize: '3rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem'
    }
  },
  heroSubtitle: {
    fontSize: '1.2rem',
    marginBottom: theme.spacing(4),
    opacity: 0.9
  },
  contentSection: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: theme.spacing(4),
    marginBottom: theme.spacing(4),
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
  },
  featureCard: {
    height: '100%',
    textAlign: 'center',
    padding: theme.spacing(3),
    borderRadius: 12,
    border: '1px solid #e0e0e0',
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
    }
  },
  featureIcon: {
    fontSize: 48,
    color: '#1760d3',
    marginBottom: theme.spacing(2)
  },
  ctaButton: {
    background: 'linear-gradient(45deg, #1760d3 30%, #17dddd 90%)',
    color: 'white',
    padding: theme.spacing(2, 4),
    fontSize: '1.1rem',
    fontWeight: 'bold',
    borderRadius: 50,
    '&:hover': {
      background: 'linear-gradient(45deg, #0d4a9e 30%, #14c4c4 90%)'
    }
  },
  statsCard: {
    background: 'linear-gradient(135deg, #17dddd 0%, #1760d3 100%)',
    color: 'white',
    textAlign: 'center',
    padding: theme.spacing(3),
    borderRadius: 12
  },
  howItWorks: {
    backgroundColor: '#f8f9fa',
    padding: theme.spacing(4),
    borderRadius: 12
  },
  stepNumber: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: '#1760d3',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    marginRight: theme.spacing(2)
  }
}));

function AffiliateProgram() {
  const classes = useStyles();

  const features = [
    {
      icon: <MoneyIcon className={classes.featureIcon} />,
      title: 'High Commission',
      description: 'Earn 100 GHS per successful school referral that uses the system for a term'
    },
    {
      icon: <PeopleIcon className={classes.featureIcon} />,
      title: 'Unlimited Referrals',
      description: 'No limit on how many schools you can refer. The more you refer, the more you earn!'
    },
    {
      icon: <TrendingIcon className={classes.featureIcon} />,
      title: 'Real-time Tracking',
      description: 'Monitor your referrals, earnings, and performance in real-time through your dashboard'
    },
    {
      icon: <BankIcon className={classes.featureIcon} />,
      title: 'Mobile Money Payments',
      description: 'Get paid via mobile money when you reach the minimum payout threshold'
    },
    {
      icon: <ShareIcon className={classes.featureIcon} />,
      title: 'Marketing Tools',
      description: 'Access professional marketing materials and tools to help you succeed'
    },
    {
      icon: <AnalyticsIcon className={classes.featureIcon} />,
      title: 'Detailed Analytics',
      description: 'Get insights into your performance with comprehensive analytics and reports'
    }
  ];

  const steps = [
    'Sign up for our affiliate program',
    'Get your unique referral link',
    'Share the link with schools',
    'Earn 100 GHS when schools register and use the system for a term',
    'Track your earnings in real-time',
    'Get paid via mobile money (minimum payout: GHS 700)'
  ];

  return (
    <div>
      {/* Hero Section */}
      <Box className={classes.container}>
        <Container maxWidth="lg">
          <Box className={classes.hero}>
            <Typography variant="h2" className={classes.heroTitle}>
              Earn Money with SIS Revolution
            </Typography>
            <Typography variant="h5" className={classes.heroSubtitle}>
              Join our affiliate program and earn commissions by referring schools to our innovative School Information System
            </Typography>
            <Box mt={4}>
              <Button
                component={Link}
                to="/affiliate/register"
                variant="contained"
                size="large"
                className={classes.ctaButton}
              >
                Become an Affiliate Today
              </Button>
            </Box>
          </Box>

          {/* Stats Section */}
          <Grid container spacing={3} style={{ marginBottom: 48 }}>
            <Grid item xs={12} sm={4}>
              <Card className={classes.statsCard}>
                <Typography variant="h3" component="h2">
                  GHS 100
                </Typography>
                <Typography variant="h6">
                  Per Successful Referral
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card className={classes.statsCard}>
                <Typography variant="h3" component="h2">
                  GHS 700
                </Typography>
                <Typography variant="h6">
                  Minimum Payout
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card className={classes.statsCard}>
                <Typography variant="h3" component="h2">
                  500+
                </Typography>
                <Typography variant="h6">
                  Active Affiliates
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" style={{ paddingTop: 64, paddingBottom: 64 }}>
        <Box className={classes.contentSection}>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            Why Choose Our Affiliate Program?
          </Typography>
          <Typography variant="h6" align="center" color="textSecondary" paragraph>
            We offer one of the most competitive affiliate programs in the education technology sector
          </Typography>

          <Grid container spacing={4} style={{ marginTop: 32 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card className={classes.featureCard}>
                  {feature.icon}
                  <Typography variant="h6" component="h3" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* How It Works Section */}
        <Box className={classes.contentSection}>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            How It Works
          </Typography>
          <Typography variant="h6" align="center" color="textSecondary" paragraph>
            Getting started is simple and straightforward
          </Typography>

          <Box className={classes.howItWorks}>
            <Grid container spacing={3}>
              {steps.map((step, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box className={classes.stepNumber}>
                      {index + 1}
                    </Box>
                    <Typography variant="body1">
                      {step}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        {/* Commission Structure */}
        <Box className={classes.contentSection}>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            Commission Structure
          </Typography>
          <Typography variant="h6" align="center" color="textSecondary" paragraph>
            Transparent and rewarding commission structure
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} style={{ padding: 24 }}>
                <Typography variant="h5" gutterBottom>
                  <SchoolIcon style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  School Referral
                </Typography>
                <Typography variant="h4" color="primary" gutterBottom>
                  GHS 100 Commission
                </Typography>
                <Typography variant="body1">
                  Earn 100 GHS when a school you refer successfully registers and uses the system for at least one term.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} style={{ padding: 24 }}>
                <Typography variant="h5" gutterBottom>
                  <TrendingIcon style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  Payout Threshold
                </Typography>
                <Typography variant="h4" color="primary" gutterBottom>
                  GHS 700 Minimum
                </Typography>
                <Typography variant="body1">
                  Request payouts when you accumulate at least 700 GHS in commissions via mobile money.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box className={classes.contentSection} textAlign="center">
          <Typography variant="h3" component="h2" gutterBottom>
            Ready to Start Earning?
          </Typography>
          <Typography variant="h6" color="textSecondary" paragraph>
            Join thousands of successful affiliates who are already earning with SIS Revolution
          </Typography>
          <Box mt={4}>
            <Button
              component={Link}
              to="/affiliate/register"
              variant="contained"
              size="large"
              className={classes.ctaButton}
              style={{ marginRight: 16 }}
            >
              Sign Up Now
            </Button>
            <Button
              component={Link}
              to="/affiliate/login"
              variant="outlined"
              size="large"
              style={{
                borderColor: '#1760d3',
                color: '#1760d3',
                borderWidth: 2
              }}
            >
              Already an Affiliate? Login
            </Button>
          </Box>
        </Box>
      </Container>
    </div>
  );
}

export default AffiliateProgram; 